import { useState, useMemo } from 'react';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import { useBasket } from '../hooks/useBasket';
import { DEFAULT_LICENSE_PERIOD_MONTHS } from '../constants';
import LicensePeriodEditor from './LicensePeriodEditor';
import { productTypeColor, productTypeLabel } from '@shared/constants/product';
import { useMapStore } from '@shared/hooks/useMapStore';
import { currencySymbol } from '@shared/pricing';
import type { BasketItem } from '../types';

interface Props {
  search?: string;
  typeFilter?: string;
  coverageFilter?: string[];
}

export default function BasketItemList({ search, typeFilter, coverageFilter }: Props) {
  const items = useBasket((s) => s.items);
  const removeItem = useBasket((s) => s.removeItem);
  const updateLicensePeriod = useBasket((s) => s.updateLicensePeriod);

  const filteredItems = useMemo(() => {
    let result: BasketItem[] = items;

    if (search) {
      const query = search.toLowerCase();
      result = result.filter((item) => item.name.toLowerCase().includes(query));
    }

    if (typeFilter && typeFilter !== 'All') {
      result = result.filter((item) => item.productType === typeFilter);
    }

    if (coverageFilter && coverageFilter.length > 0) {
      result = result.filter((item) => item.coverage && coverageFilter.includes(item.coverage));
    }

    return result;
  }, [items, search, typeFilter, coverageFilter]);

  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        Basket is empty. Add products from Catalog, Routes, or Holdings.
      </Typography>
    );
  }

  if (filteredItems.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        No items match the current filters.
      </Typography>
    );
  }

  return (
    <div data-testid="basket-item-list">
      <List disablePadding>
        {filteredItems.map((item) => (
          <BasketItemRow
            key={item.productId}
            item={item}
            onRemove={() => removeItem(item.productId)}
            onPeriodChange={(months) => updateLicensePeriod(item.productId, months)}
          />
        ))}
      </List>
    </div>
  );
}

/* ------------------------------------------------------------------ */

interface RowProps {
  item: BasketItem;
  onRemove: () => void;
  onPeriodChange: (months: number) => void;
}

function BasketItemRow({ item, onRemove, onPeriodChange }: RowProps) {
  const selectedProductId = useMapStore((s) => s.selectedProductId);
  const setSelectedProductId = useMapStore((s) => s.setSelectedProductId);
  const [periodEditing, setPeriodEditing] = useState(false);
  const availablePeriods = item.availablePrices?.map((p) => p.periodMonths);
  const periodMonths = item.licensePeriodMonths ?? DEFAULT_LICENSE_PERIOD_MONTHS;

  return (
    <ListItemButton
      selected={selectedProductId === item.productId}
      onClick={() => setSelectedProductId(item.productId)}
      sx={{
        flexDirection: 'column',
        alignItems: 'stretch',
        py: 1,
        px: 2,
        borderBottom: 1,
        borderColor: 'divider',
        gap: 0,
      }}
    >
      {/* Row 1: [Badge] [Name]                [Price] */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box
          sx={{
            px: 0.5,
            py: 0.125,
            borderRadius: 0.5,
            bgcolor: productTypeColor[item.productType],
            color: 'common.white',
            fontSize: '0.6rem',
            fontWeight: 700,
            lineHeight: 1.2,
            flexShrink: 0,
          }}
        >
          {productTypeLabel[item.productType]}
        </Box>
        <Typography
          variant="body2"
          noWrap
          component="div"
          sx={{ fontSize: '0.875rem', lineHeight: 1.4, fontWeight: 500, flex: 1, minWidth: 0 }}
        >
          {item.name}
        </Typography>
        {item.priceAmount != null && item.currencyCode && (
          <Typography
            variant="caption"
            component="span"
            sx={{ fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            {currencySymbol(item.currencyCode)}
            {item.priceAmount.toFixed(2)}
          </Typography>
        )}
      </Box>

      {/* Row 2: [Title]                       [Remove] */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ flex: 1, minWidth: 0 }}
          component="div"
        >
          {item.title ?? ''}
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          sx={{ flexShrink: 0, p: 0.25 }}
        >
          <CloseIcon sx={{ fontSize: '0.875rem' }} />
        </IconButton>
      </Box>

      {/* Row 3: AVCS Period (inline text → select on click) */}
      {item.productType === 'AVCS' &&
        (periodEditing ? (
          <LicensePeriodEditor
            value={periodMonths}
            onChange={(months) => {
              onPeriodChange(months);
              setPeriodEditing(false);
            }}
            availablePeriods={availablePeriods}
            autoOpen
            onBlur={() => setPeriodEditing(false)}
          />
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' }, fontSize: '0.7rem' }}
            onClick={(e) => {
              e.stopPropagation();
              setPeriodEditing(true);
            }}
          >
            Period: {periodMonths} months
          </Typography>
        ))}
    </ListItemButton>
  );
}
